import { Box, Typography } from '@mui/material';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import TabletHandler from 'handlers/tablet.handler';
import { useState } from 'react';
import type { Tablet } from 'types/tablet.types';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import { Selector } from 'components/Form/Selectors/Selector';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { modalInputFieldStyle } from 'styles/global.css';
import { useTranslation } from 'react-i18next';

interface Props {
  title: string;
  open: boolean;
  tabletData: Tablet;
  handleAbort: () => void;
}

const FALLBACK = 'Unknown';

const STATUS_OPTIONS = [
  { display: 'Active', id: 'active' },
  { display: 'Inactive', id: 'inactive' },
];

export const TabletInfoModal = ({
  title,
  handleAbort,
  open,
  tabletData,
}: Props) => {
  const { t } = useTranslation();
  const tabletHandler = new TabletHandler();
  const { timezoneBasedOnDisplayMode, preferredTimeDisplayMode } =
    useTimeDisplayMode();

  const [form, setForm] = useState({
    appVersion: tabletData.appVersion ?? '',
    status: tabletData.status ?? 'active',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    await tabletHandler.updateTablet(tabletData.tabletId, form);
    handleAbort();
  };

  return (
    <InteractiveModal
      initialState={open}
      SuccessButtonProps={{
        disabled: false,
        onClick: handleSubmit,
        label: 'SAVE EDITS',
      }}
      handleAbort={handleAbort}
      title={title}
      confirmCancellation
    >
      <Box key="tabletId">
        <Typography variant="bodyBold" display="block" mb="4px">
          {t('tablet_id')}
        </Typography>
        <Typography variant="medium">{tabletData.tabletId}</Typography>
      </Box>

      <Box key="deviceId">
        <Typography variant="bodyBold" display="block" mb="4px">
          {t('device_id')}
        </Typography>
        <Typography variant="medium">
          {tabletData.deviceId ?? FALLBACK}
        </Typography>
      </Box>

      <Box key="bumperId">
        <Typography variant="bodyBold" display="block" mb="4px">
          {t('bumper_id_10')}
        </Typography>
        <Typography variant="medium">
          {tabletData.bumperId ?? FALLBACK}
        </Typography>
      </Box>

      <Box key="status">
        <Typography variant="bodyBold" display="block" mb="4px">
          {t('cdp_step_status')}
        </Typography>
        <Selector
          value={form.status}
          onChange={(e) => {
            setForm((prev) => ({
              ...prev,
              status: e.target.value as Tablet['status'],
            }));
          }}
          selectorOptions={STATUS_OPTIONS}
        />
      </Box>

      <Box key="appVersion">
        <SWTextField
          name="appVersion"
          label={t('app_version')}
          TextFieldProps={{
            name: 'appVersion',
            value: form.appVersion,
            onChange: handleChange,
            InputProps: { sx: { ...modalInputFieldStyle, minWidth: '297px' } },
          }}
        />
      </Box>

      <Box key="lastSync">
        <Typography variant="bodyBold" display="block" mb="4px">
          Last Sync
        </Typography>
        <Typography variant="medium">
          {tabletData.lastSync
            ? convertUtcToDisplayTime(
                tabletData.lastSync,
                preferredTimeDisplayMode,
                timezoneBasedOnDisplayMode
              )
            : FALLBACK}
        </Typography>
      </Box>
    </InteractiveModal>
  );
};
